import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from "moment";

//components
import { toast } from "react-toastify";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Button, CloseButton, Col, Form, Row } from "react-bootstrap";
import { Modal } from "../../../../components/modal";
import Error from "../../../../components/Error";

//utils
import { FaPlusCircle } from "react-icons/fa";
import { formatDateDB } from "../../../../../helpers";
import { createUser } from "../../../../services/manager/user.service";
import {
	newSubscription,
	getPlans,
	getPlanDetails,
} from "../../../../services/manager/signature.service";

const validationSchema = Yup.object({
	startDate: Yup.date().required("Este campo é obrigatório"),
	endDate: Yup.date()
		.when(
			"startDate",
			(startDate, schema) =>
				startDate &&
				schema.min(
					startDate,
					"A data final precisa ser maior do que a data inicial"
				)
		)
		.required("Este campo é obrigatório"),
	contractValue: Yup.string().required("Este campo é obrigatório"),
	attachedDocs: Yup.array(),
	plan: Yup.string().required("Este campo é obrigatório"),
}).required();

export const ModalSignature = (props: any): JSX.Element => {
	const { t: translation } = useTranslation();
	const {
		showModal,
		setShowModal,
		isNewSignture = true,
		user,
		loading,
		setLoading,
		userObj,
		setCleanFields,
		setResponseSignature,
	} = props;
	const {
		handleSubmit,
		control,
		reset,
		setValue,
		setError,
		formState: { errors: errors },
	} = useForm({ resolver: yupResolver(validationSchema) });

	const inputFile = useRef<HTMLInputElement | null>(null);

	const [disableFields, setDisableFields] = useState<boolean>(true);
	const [currencyValue, setCurrencyValue] = useState<any>(null);
	const [attachedDocs, setAttachedDocs] = useState<any[]>([]);
	const [planSelected, setPlanSelected] = useState<string>("");
	const [planOptions, setPlanOptions] = useState<any[]>([]);
	const [minimumContractTime, setMinimumContractTime] = useState<number>(0);

	const onSubmit = (formData: any) => {
		const monthDiff = moment(formData.endDate)
			.add(1, "seconds")
			.diff(formData.startDate, "months");

		if (monthDiff < minimumContractTime) {
			setError("startDate", {
				type: "custom",
				message: `O período selecionado é inválido. O período mínimo é de ${minimumContractTime} meses`,
			});
			return;
		}

		setLoading(true);

		if (user) {
			let obj = {
				start: formatDateDB(formData.startDate),
				end: formatDateDB(formData.endDate),
				value: formData.contractValue,
				subscribe: user.id,
				files: attachedDocs,
				plan: planSelected,
			};

			newSubscription(obj).then((response) => {
				if (setResponseSignature) {
					setResponseSignature(response);
				}
				toast.success("Assinatura criada com sucesso!");
				reset();
				setAttachedDocs([]);
				setCurrencyValue(null);
				setShowModal(false);
				setLoading(false);
				setDisableFields(true);
				setPlanSelected("");
				setMinimumContractTime(0);
			});
		} else {
			setCleanFields(false);
			createUser(userObj).then((response: any) => {
				let obj = {
					start: formatDateDB(formData.startDate),
					end: formatDateDB(formData.endDate),
					value: formData.contractValue,
					subscribe: response.id,
					files: attachedDocs,
				};

				newSubscription(obj).then((_response) => {
					if (setResponseSignature) {
						setResponseSignature(_response);
					}
					toast.success("Assinatura criada com sucesso!");
					reset();
					setAttachedDocs([]);
					setCurrencyValue(null);
					setShowModal(false);
					setLoading(false);
					setCleanFields(true);
					setPlanSelected("");
					setMinimumContractTime(0);
				});
			});
		}
	};

	const handleAddDocs = (docs: any) => {
		setAttachedDocs([...attachedDocs, ...docs]);
		setValue("attachedDocs", [...attachedDocs, ...docs]);
	};

	const handleRemoveDoc = (indexDoc: any) => {
		let arr = attachedDocs.slice(0);
		arr.splice(indexDoc, 1);
		setAttachedDocs(arr);
		setValue("attachedDocs", arr);
	};

	const handleSetPlanDetails = (planId: number) => {
		getPlanDetails(planId).then((plan: any) => {
			let date = new Date();
			date.setMonth(new Date().getMonth() + 12);

			setCurrencyValue(plan.parcel);
			setMinimumContractTime(plan.minimum_contract);
			setValue("contractValue", plan.parcel);
			setValue("startDate", new Date());
			setValue("endDate", date);
		});
	};

	useEffect(() => {
		getPlans("status=True").then((plans: any) => {
			let arr: any = [];

			plans.map((plan: any) => {
				let obj = {
					value: plan.id,
					label: plan.name,
				};
				arr.push(obj);
			});

			setPlanOptions(arr);
		});
	}, []);

	return (
		<Modal
			headerTitle={
				isNewSignture
					? translation("manager.subscription.new")
					: translation("manager.subscription.renew")
			}
			submitButtonText={translation("save")}
			show={showModal}
			onHide={() => setShowModal(false)}
			onSubmit={handleSubmit(onSubmit)}
			variant="form-modal-default"
			loading={loading}
		>
			<Form>
				<Row>
					<Col
						xs={12}
						className="mb-3 text-black"
					>
						<h6>
							<b>
								{translation("manager.subscription.userData")}
							</b>
						</h6>
					</Col>

					<Col
						xs={12}
						className="mb-3 text-darker-grey"
					>
						<p>
							<b>
								{translation("manager.subscription.company")}:
							</b>{" "}
							{user?.name}
						</p>
						<p>
							<b>
								{translation(
									"manager.subscription.responsible"
								)}
								:
							</b>{" "}
							{user?.username ?? "Nenhum responsável informado"}
						</p>
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-black"
					>
						<Form.Group className="mb-2">
							<Form.Label className="text-darker-grey">
								{translation("manager.subscription.plan")}:
							</Form.Label>
							<>
								<Dropdown
									className="select-form-default"
									value={planSelected}
									options={planOptions}
									onChange={(e) => {
										setValue("plan", e.value);
										setPlanSelected(e.value);
										setDisableFields(false);
										handleSetPlanDetails(e.value);
									}}
									placeholder="Selecione um plano"
								/>
							</>
							{errors.plan ? (
								<div className="w-100">
									{" "}
									<Error>{errors.plan?.message}</Error>{" "}
								</div>
							) : (
								""
							)}
						</Form.Group>
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-black"
					>
						<Form.Label>
							{translation("manager.subscription.contractValue")}:
						</Form.Label>
						<br />
						<InputNumber
							disabled={disableFields}
							value={currencyValue}
							onValueChange={(e: any) => {
								setCurrencyValue(e.value);
								setValue("contractValue", e.value);
							}}
							mode="currency"
							currency="BRL"
							locale="pt-BR"
							placeholder="R$ 1.000,00"
							style={{ width: "100%", height: "40px" }}
						/>
						{errors.contractValue ? (
							<div className="w-100">
								{" "}
								<Error>
									{errors.contractValue?.message}
								</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-black"
					>
						<Form.Label>
							{translation("manager.subscription.start")}:
						</Form.Label>
						<br />
						<Controller
							control={control}
							name="startDate"
							render={({ field }) => (
								<Calendar
									disabled={disableFields}
									value={field.value}
									onChange={field.onChange}
									onMonthChange={() => console.log()}
									dateFormat="dd/mm/yy"
									locale={"pt-BR"}
									placeholder="Selecione..."
									style={{ width: "100%", height: "40px" }}
								/>
							)}
						/>
						{errors.startDate ? (
							<div className="w-100">
								{" "}
								<Error>{errors.startDate?.message}</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-black"
					>
						<Form.Label>
							{translation("manager.subscription.end")}:
						</Form.Label>
						<br />
						<Controller
							control={control}
							name="endDate"
							render={({ field }) => (
								<Calendar
									disabled={disableFields}
									value={field.value}
									onChange={field.onChange}
									onMonthChange={() => console.log()}
									dateFormat="dd/mm/yy"
									locale={"pt-BR"}
									placeholder="Selecione..."
									style={{ width: "100%", height: "40px" }}
								/>
							)}
						/>
						{errors.endDate ? (
							<div className="w-100">
								{" "}
								<Error>{errors.endDate?.message}</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={6}
						className="mt-4 text-black"
					>
						<Form.Label>
							{translation("manager.subscription.attachedDocs")}
						</Form.Label>
					</Col>
					<Col
						xs={6}
						className="mt-4 text-black text-end"
					>
						<input
							ref={inputFile}
							className="d-none"
							type="file"
							multiple
							onChange={(e: any) =>
								handleAddDocs(e.currentTarget.files)
							}
						/>
						<Button
							variant="link"
							onClick={() => inputFile.current?.click()}
							style={{ textDecoration: "none" }}
						>
							{translation("manager.subscription.add")}{" "}
							<FaPlusCircle />
						</Button>
					</Col>

					<Col
						xs={12}
						className="mt-2 text-black doc-div"
					>
						<Row>
							{attachedDocs.map((doc: any, index: number) => (
								<Col
									xs={12}
									md={6}
									lg={4}
									key={index}
									className="mt-2 mb-2 doc-attached"
								>
									<CloseButton
										className="close-button-doc-attached"
										onClick={() => handleRemoveDoc(index)}
									/>{" "}
									{doc.name}
								</Col>
							))}
						</Row>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};
